<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetAccountGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const currencies = ref(null);
  const accountTypes = ref(null);
  const roundingTypes = ref([
    {
      value: null,
      text: $fn.value.pgettext('RoundingType', 'no rounding'),
    },
    {
      value: 1,
      text: $fn.value.pgettext('RoundingType', 'round to 1'),
    },
    {
      value: 5,
      text: $fn.value.pgettext('RoundingType', 'round to 5'),
    },
    {
      value: 10,
      text: $fn.value.pgettext('RoundingType', 'round to 10'),
    },
  ]);
  const ux = ref([]);
  const b_4615685607825208_parameters = computed(() => ({
    role: 'own',
  }));

  const $data = computed(() => ({
    currencies: currencies.value,
    accountTypes: accountTypes.value,
    roundingTypes: roundingTypes.value,
    ux: ux.value,
    b_4615685607825208_parameters: b_4615685607825208_parameters.value,
  }));

  const $$load_currencies = () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_accountTypes = () => {
    return $fn.value.getEnumValuesFromSchema('AccountTypes');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'currencies' },
      { name: 'accountTypes' },
      { name: 'roundingTypes' },
      { name: 'ux' },
      { name: 'b_4615685607825208_parameters', nodeId: '4615685607825208' },
    ],
    loaders: {
      currencies: $$load_currencies,
      accountTypes: $$load_accountTypes,
    },
    ux: {},
    dataRefs: {
      currencies,
      accountTypes,
      roundingTypes,
      ux,
      b_4615685607825208_parameters,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadOnly = computed(() => {
    return props.item.locked;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_7315092382398562_reloadComponentData_5yc6qj9bj =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_lq0mgfgk6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_5yc6qj9bj,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_lq0mgfgk6],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_lq0mgfgk6,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_lq0mgfgk6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_lq0mgfgk6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpRow>
      <ItpCol :w="6">
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="owner"
          :label="gettext('Owner')"
          :labelAlign="labelAlign"
        >
          <UcCompanySelector
            :item="item"
            path="owner"
            :parameters="b_4615685607825208_parameters"
            readonly
            disabled
            autofocus
          >
          </UcCompanySelector>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="account_type"
          :label="gettext('Account type')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormNativeSelect
            name="account_type"
            v-model="item.account_type"
            :options="accountTypes"
            :readonly="isReadOnly"
            required
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="name"
          :label="gettext('Name')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormInput
            name="name"
            v-model="item.name"
            :readonly="isReadOnly"
            required
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="5"
          name="account_type"
          :label="gettext('Default')"
          :labelAlign="labelAlign"
        >
          <ItpFormCheckbox
            name="is_default"
            inline
            v-model="item.is_default"
            :readonly="isReadOnly"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="5"
          name="currency"
          :label="gettext('Currency')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormNativeSelect
            name="currency"
            v-model="item.currency"
            :options="currencies"
            valueField="code"
            textField="code"
            :readonly="isReadOnly"
            required
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="5"
          name="round_to"
          :label="pgettext('Account', 'Rounding')"
          :labelAlign="labelAlign"
        >
          <ItpFormNativeSelect
            name="round_to"
            v-model="item.round_to"
            :options="roundingTypes"
            :readonly="isReadOnly"
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="5"
          name="invoice_settlement_rounding_tolerance"
          :label="pgettext('Account', 'Invoice settlement tolerance')"
          :labelAlign="labelAlign"
          :helpTooltip="
            pgettext(
              'Account',
              'The excess settlement amount to allow on payments.'
            )
          "
        >
          <ItpFormInput
            type="number"
            name="invoice_settlement_rounding_tolerance"
            v-model="item.invoice_settlement_rounding_tolerance"
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="payment_in_numbering"
          :label="pgettext('Account', 'Numbering (Pay in)')"
          :labelAlign="labelAlign"
          v-if="item.object"
        >
          <NumberingSelector
            name="payment_in_numbering"
            v-model="item.payment_in_numbering"
            :readonly="isReadOnly"
            :objectType="item.object"
          >
          </NumberingSelector>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="payment_out_numbering"
          :label="pgettext('Account', 'Numbering (Pay out)')"
          :labelAlign="labelAlign"
          v-if="item.object"
        >
          <NumberingSelector
            name="payment_out_numbering"
            v-model="item.payment_out_numbering"
            :readonly="isReadOnly"
            :objectType="item.object"
          >
          </NumberingSelector>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="description"
          :label="gettext('Description')"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea
            name="description"
            v-model="item.description"
            :readonly="isReadOnly"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpCol>
      <ItpCol :w="6">
        <ItpFormGroup
          :label-cols-md="3"
          :content-cols-md="9"
          name="account_number"
          :label="gettext('Account number')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            name="account_number"
            v-model="item.account_number"
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="3"
          :content-cols-md="9"
          name="iban"
          :label="gettext('IBAN')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput name="iban" v-model="item.iban" :readonly="isReadOnly">
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="3"
          :content-cols-md="9"
          name="swift"
          :label="gettext('SWIFT')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            name="swift"
            v-model="item.swift"
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="3"
          :content-cols-md="9"
          name="bank"
          :label="gettext('Bank')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput name="bank" v-model="item.bank" :readonly="isReadOnly">
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="3"
          :content-cols-md="9"
          name="branch"
          :label="gettext('Branch')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            name="branch"
            v-model="item.branch"
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
      </ItpCol>
    </ItpRow>
  </ItpUsercontrol>
</template>
